import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';

export type HeroGalleryView = 'full' | 'detail' | 'plate';

/** One image in the mobile carousel / full-screen gallery. src '' = live plate render (or not ready). */
export interface HeroGallerySlide {
  kind: HeroGalleryView;
  src: string;
  alt: string;
}

// Final production data shape for the hero gallery. The page supplies these as
// URLs; the gallery is purely presentational and never generates them.
//   full         -> full Plate Viewer vehicle render (default main image)
//   detailMain   -> promoted zoom crop (fill 0.48) shown when the tile is clicked
//   detailTile   -> thumbnail-only zoom crop (fill 0.70) — NEVER promoted itself
//   renderedPlate-> rendered plate image; when empty the gallery falls back to
//                   the live <pla-plate> render (existing PlateX plate, not a
//                   new graphic)
export interface HeroGallery {
  full: string;
  detailMain: string;
  detailTile: string;
  renderedPlate: string;
}

// Presentational hero gallery for the plate-detail-v2 page. URL-driven only:
// it consumes the gallery image model supplied by the page and the existing
// <pla-plate> render. It does NOT generate images, crop, or run Three.js.
//
// Swap gallery: there are three images total (full vehicle, zoom crop,
// rendered plate). The selected one fills the large main slot; the other two
// sit below as the two thumbnails. Clicking a thumbnail promotes it to main and
// the previous main drops back into the row, so exactly one main + two
// thumbnails are shown at all times.
//   default            -> main = full,  thumbs = zoom + plate
//   click zoom tile     -> main = detailMain (the 0.48 crop; the 0.70 TILE crop
//                          is the thumbnail only and is NEVER promoted)
//   click plate tile    -> main = renderedPlate (or live <pla-plate> fallback)
//   click main image    -> opens the current main image in a lightbox
@Component({
  selector: 'pla-pdv2-hero-gallery',
  templateUrl: './hero-gallery.component.html',
  styleUrls: ['./hero-gallery.component.scss'],
})
export class HeroGalleryComponent implements OnDestroy {
  // Registration to render via the existing <pla-plate> component (used for the
  // live plate render when no renderedPlate image URL is supplied).
  @Input() public registration: string;

  // Gallery image model (final production shape). Individual fields may be empty
  // while the crop cache backend is not yet wired; the UI degrades gracefully.
  @Input() public gallery: HeroGallery | null = null;

  // The large main image is the full on-vehicle render. The thumbnail row below
  // holds the rendered plate (left) and the zoom-detail crop (right).
  public view: HeroGalleryView = 'full';

  private get full(): string {
    return this.gallery?.full || '';
  }
  public get fullSrc(): string {
    return this.full;
  }
  private get detailMain(): string {
    return this.gallery?.detailMain || '';
  }
  public get detailTileSrc(): string {
    return this.gallery?.detailTile || '';
  }
  private get renderedPlate(): string {
    return this.gallery?.renderedPlate || '';
  }

  // The zoom tile only exists when both its thumbnail (0.70) and its promoted
  // image (0.48) are available — the tile crop is never promoted on its own.
  public get hasZoom(): boolean {
    return !!this.detailTileSrc && !!this.detailMain;
  }

  public get mainSrc(): string | null {
    if (this.view === 'detail') return this.detailMain || this.full;
    if (this.view === 'plate') return this.renderedPlate || null;
    return this.full || null;
  }

  // Show the live plate render in the main stage only when the plate view is
  // active and no rendered-plate image was supplied.
  public get showPlateInMain(): boolean {
    return this.view === 'plate' && !this.renderedPlate;
  }

  // Skeleton state: the selected main image is an <img> but its URL is not ready
  // yet (e.g. the full render is still generating). The live <pla-plate> render
  // never loads, so it is excluded.
  public get mainLoading(): boolean {
    return !this.showPlateInMain && !this.mainSrc;
  }

  public showFull(): void {
    this.view = 'full';
  }

  public showZoom(): void {
    if (this.hasZoom) this.view = 'detail';
  }

  public showPlate(): void {
    this.view = 'plate';
  }

  // ---- slides (mobile carousel + full-screen gallery) ----
  // Same order as the desktop cluster reads: vehicle, plate, close-up. The
  // vehicle slide is always present (skeleton until its render is ready), like
  // the desktop main stage; the close-up only when its crop exists.
  private slideCache: HeroGallerySlide[] = [];
  private slideKey = '';

  public get slides(): HeroGallerySlide[] {
    const key = [this.registration, this.full, this.renderedPlate, this.detailMain, this.hasZoom].join('|');
    if (key !== this.slideKey) {
      this.slideKey = key;
      const reg = this.registration || '';
      const out: HeroGallerySlide[] = [
        { kind: 'full', src: this.full, alt: `${reg} on an illustrative vehicle (PlateX visualisation)` },
        { kind: 'plate', src: this.renderedPlate, alt: `${reg} number plate` },
      ];
      if (this.hasZoom) {
        out.push({ kind: 'detail', src: this.detailMain, alt: `${reg} plate detail (PlateX visualisation)` });
      }
      this.slideCache = out;
    }
    return this.slideCache;
  }

  public trackSlide(_: number, s: HeroGallerySlide): string {
    return s.kind;
  }

  // Mobile carousel position, derived from the native scroll (scroll-snap).
  @ViewChild('track') private track?: ElementRef<HTMLElement>;
  public slideIndex = 0;

  public onTrackScroll(): void {
    const el = this.track?.nativeElement;
    if (!el || !el.clientWidth) return;
    const i = Math.round(el.scrollLeft / el.clientWidth);
    if (i !== this.slideIndex) this.slideIndex = i;
  }

  public goTo(i: number): void {
    const el = this.track?.nativeElement;
    if (!el) return;
    el.scrollTo({ left: i * el.clientWidth, behavior: 'smooth' });
  }

  // ---- full-screen gallery ----
  public lightboxOpen = false;
  public lbIndex = 0;
  @ViewChild('lbTrack') private lbTrack?: ElementRef<HTMLElement>;

  // Desktop: open at whatever is in the main slot.
  public openLightbox(): void {
    if (this.mainLoading) return;
    const i = this.slides.findIndex((s) => s.kind === this.view);
    this.openLightboxAt(i < 0 ? 0 : i);
  }

  // Mobile: open at the tapped slide (skipped while its image is still loading).
  public openLightboxAt(i: number): void {
    const slide = this.slides[i];
    if (!slide || (slide.kind !== 'plate' && !slide.src)) return;
    this.lbIndex = i;
    this.lightboxOpen = true;
    document.body.style.overflow = 'hidden';
    // The track only exists once *ngIf has rendered; jump (no animation) to
    // the requested slide on the next tick.
    setTimeout(() => {
      const el = this.lbTrack?.nativeElement;
      if (el) el.scrollLeft = i * el.clientWidth;
    });
  }

  public onLbScroll(): void {
    const el = this.lbTrack?.nativeElement;
    if (!el || !el.clientWidth) return;
    const i = Math.round(el.scrollLeft / el.clientWidth);
    if (i !== this.lbIndex) this.lbIndex = i;
  }

  public lbGo(i: number): void {
    const el = this.lbTrack?.nativeElement;
    if (!el || i < 0 || i >= this.slides.length) return;
    el.scrollTo({ left: i * el.clientWidth, behavior: 'smooth' });
  }

  @HostListener('document:keydown', ['$event'])
  public onKey(e: KeyboardEvent): void {
    if (!this.lightboxOpen) return;
    if (e.key === 'ArrowRight') this.lbGo(this.lbIndex + 1);
    else if (e.key === 'ArrowLeft') this.lbGo(this.lbIndex - 1);
  }

  @HostListener('document:keydown.escape')
  public closeLightbox(): void {
    if (!this.lightboxOpen) return;
    this.lightboxOpen = false;
    document.body.style.overflow = '';
  }

  public ngOnDestroy(): void {
    this.closeLightbox();
  }
}
